<template>
  <v-list-item :ripple="hasOpenDetailListener()" @click="hasOpenDetailListener() ? emitOpenDetail() : undefined">
    <slot name="icon"></slot>
    <v-list-item-content>
      <v-list-item-subtitle v-if="!small">
        {{
          item.isSigned
            ? $t("sign.DocumentDetail.signed")
            : item.isTemplate
              ? $t("sign.DocumentDetail.template")
              : $t("sign.DocumentDetail.file")
        }}
      </v-list-item-subtitle>
      <v-list-item-title class="text-truncate">
        {{ item.title }}
      </v-list-item-title>
      <v-list-item-subtitle v-if="item.tags && item.tags.length">
        <tag-preview :value="item.tags" size="x-small" />
      </v-list-item-subtitle>

      <v-list-item-subtitle
        v-if="item.description && item.description !== 'undefined' && !small"
        class="text-truncate text-xs"
      >
        {{ item.description }}
      </v-list-item-subtitle>

      <div v-if="!small">
        <vue-picture-swipe
          v-if="!isLoading"
          class="mx-2 mt-2 mb-2"
          :items="images"
          :options="{
            shareEl: false,
            rotationOn: true,
            showAnimationDuration: 0,
            hideAnimationDuration: 0,
            history: false
          }"
        />
        <v-skeleton-loader v-else type="image" class="mx-2 mt-2 mb-2" />
      </div>
    </v-list-item-content>
    <v-list-item-action v-if="hasOpenDetailListener()">
      <v-icon small>mdi-chevron-right</v-icon>
    </v-list-item-action>
  </v-list-item>
</template>

<script lang="ts">
import { convertPdfToImg } from "@/lib/utility/convertPdfToImage";
import { detailedDateWithDay, formatHoursAndMinutes, simpleDate } from "@/lib/utility/date-helper";
import { GoToHelper } from "@/lib/utility/goToHelper";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { ISignDocument } from "@/models/sign-document.entity";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import TagPreview from "../tag/TagPreview.vue";
import { IRefDetail } from "./RefsList.vue";

@Component({
  components: { TagPreview },
  filters: {
    simpleDate,
    detailedDateWithDay,
    formatHoursAndMinutes
  }
})
export default class RefsDocument extends mixins(PermissionMixin) implements IRefDetail<ISignDocument> {
  @Prop()
  item!: ISignDocument;

  @Prop({ default: false })
  small!: boolean;

  @Prop({ default: false })
  confirmable!: boolean;

  hasOpenDetailListener() {
    return Boolean(this.$listeners && this.$listeners.openDetail);
  }

  emitOpenDetail() {
    this.$emit("openDetail", this.item);
  }

  isLoading = false;

  images: {
    src: string;
    thumbnail: string;
    w: number;
    h: number;
    title: string;
  }[] = [];

  async mounted() {
    if (this.small) return;

    try {
      this.isLoading = true;

      const pages = await convertPdfToImg(this.item.url);
      this.images.slice(0);

      pages.forEach((i, index) => {
        this.images.push({
          src: i,
          thumbnail: i,
          w: 0,
          h: 0,
          title: `${document?.title} ${index + 1}/${pages.length}`
        });
      });
    } catch (e) {
      this.$log.error(e);
      await this.fetchAndConvert();
    } finally {
      this.isLoading = false;
    }
  }

  async fetchAndConvert() {
    try {
      this.isLoading = true;
      this.can(ActionEnum.READ, ResourceEnum.SIGN);
      await this.item.fetch();
      const pages = await convertPdfToImg(this.item.url);
      this.images.slice(0);

      pages.forEach((i, index) => {
        this.images.push({
          src: i,
          thumbnail: i,
          w: 0,
          h: 0,
          title: `${document?.title} ${index + 1}/${pages.length}`
        });
      });
    } catch (e) {
      this.$log.error(e);
    } finally {
      this.isLoading = false;
    }
  }

  goToDetail() {
    new GoToHelper(this.$router).goToDocumentDetail(this.item.id, this.item.partnerId, true);
  }
}
</script>
